import React, { useRef, useState, useEffect } from 'react';
import { FaMinus } from 'react-icons/fa';
import { uploadToS3 } from '../../../config/aws';
import { USER_NAME } from '../../../constant';
import { sendToBackend } from '../../../utils/utilities';
import { showSuccessAlert, showErrorAlert, showWarningAlert, showInfoAlert, showConfirmDialog } from '../../../utils/sweetAlertConfig';
import '../../../styles/sweetAlert.css';
import { checkAvailableCredits, decreaseCredits } from '../../../dbApiFunctions';

const CreateNewStyle = ({ onRequestClose, fetchStyles, setCreditsAvailable }) => {
  const fileInputRef = useRef(null);
  const dropdownRef = useRef(null);
  const [uploadedImages, setUploadedImages] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [styleName, setStyleName] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [rembgWaitTime, setRembgWaitTime] = useState(0);

  const categories = {
    'Interior Design': {
      'Modern': ['Minimalist', 'Contemporary', 'Industrial', 'Scandinavian'],
      'Traditional': ['Classic', 'Rustic', 'Vintage', 'Mediterranean']
    }
  };

  //   // Load images from sessionStorage on component mount
  //   useEffect(() => {
  //     const storedStyles = sessionStorage.getItem('uploadedStyles');
  //     if (storedStyles) {
  //       const parsedStyles = JSON.parse(storedStyles);
  //       setUploadedImages(parsedStyles.map(style => ({
  //         ...style,
  //         preview: URL.createObjectURL(new Blob([style.data], { type: 'image/jpeg' }))
  //       })));
  //     }
  //   }, []);

  //   // Save images to sessionStorage whenever uploadedImages changes
  //   useEffect(() => {
  //     if (uploadedImages.length > 0) {
  //       const stylesToStore = uploadedImages.map(image => ({
  //         name: styleName,
  //         category: selectedCategory,
  //         data: image.file,
  //         preview: image.preview
  //       }));
  //       sessionStorage.setItem('uploadedStyles', JSON.stringify(stylesToStore));
  //     }
  //   }, [uploadedImages, styleName, selectedCategory]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleDragOver = (event) => {
    event.preventDefault();
    event.currentTarget.classList.add('bg-gray-200');
  };

  const handleDragLeave = (event) => {
    event.currentTarget.classList.remove('bg-gray-200');
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.currentTarget.classList.remove('bg-gray-200');
    const files = event.dataTransfer.files;
    handleFiles(files);
  };

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const handleChange = (event) => {
    const files = event.target.files;
    handleFiles(files);
  };

  const handleFiles = (files) => {
    const newImages = Array.from(files).map(file => ({
      file,
      preview: URL.createObjectURL(file)
    }));
    setUploadedImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setUploadedImages(prev => {
      const newImages = [...prev];
      URL.revokeObjectURL(newImages[index].preview);
      newImages.splice(index, 1);
      return newImages;
    });
  };

  const handleCategorySelect = (category) => {
    setSelectedCategory(category);
    setShowCategoryDropdown(false);
  };

  const handleStyleNameChange = (e) => {
    setStyleName(e.target.value);
  };

  const handleUpload = async () => {
    if (!styleName) {
      showWarningAlert('Please enter a style name', 'Style Name Required');
      return;
    }

    if (uploadedImages.length === 0) {
      showWarningAlert('Please upload at least one image', 'Images Required');
      return;
    }
    setIsUploading(true);

    const email = sessionStorage.getItem('email');
    const password = sessionStorage.getItem('password');
    const response = await checkAvailableCredits(email, password, 10);
    if (!response.status) {
      showErrorAlert(response.message, 'Insufficient Credits');
      return;
    }

    try {
      for (const image of uploadedImages) {
        const file = image.file;
        if (!file) {
          console.error('Invalid file object');
          continue;
        }
        const s3Path = `USERS/${USER_NAME}/all_styles/${styleName}/user_style_images/${file.name}`;
        await uploadToS3(s3Path, file.type, file);
      }
      // console.log('Images uploaded successfully!');

      // Save metadata to S3
      const s3Path = `USERS/${USER_NAME}/all_styles/${styleName}/metadata.json`;
      const metadata = {
        styleName: styleName,
        category: selectedCategory,
        images: uploadedImages.map(image => image.file.name)
      };
      await uploadToS3(s3Path, 'application/json', JSON.stringify(metadata));

      const response = await sendToBackend('newStyle', {
        styleName: styleName,
        category: selectedCategory,
        userName: USER_NAME
      }, 10, setCreditsAvailable);

      // setUploadedImages([]);
      // setStyleName('');
      // setSelectedCategory('');
      fetchStyles();
      onRequestClose();
      showSuccessAlert('Style Created Successfully!', 'Your style has been created and is being processed.');

    } catch (error) {
      console.error('Error uploading images:', error);
      showErrorAlert('Error uploading images. Please try again.', 'Upload Failed');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="w-full">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <h2 className="text-lg font-semibold mb-4">Style Guidelines</h2>
          <ul className="space-y-2 mb-6">
            <li className="flex items-center text-green-600">
              <i className="fas fa-check-circle mr-2"></i>
              Show photos demonstrating the style's characteristics.
            </li>
            <li className="flex items-center text-green-600">
              <i className="fas fa-check-circle mr-2"></i>
              Include multiple angles and details.
            </li>
            <li className="flex items-center text-green-600">
              <i className="fas fa-check-circle mr-2"></i>
              Show how the style works in different settings.
            </li>
            <li className="flex items-center text-red-600">
              <i className="fas fa-times-circle mr-2"></i>
              Do not upload images that don't match the style.
            </li>
          </ul>

          <h2 className="text-lg font-semibold mb-4">Style Properties</h2>
          <div className="mb-4">
            <label htmlFor="style-name" className="block text-gray-700 mb-2" >Name Your Style</label>
            <input
              type="text"
              id="style-name"
              placeholder="Style Name"
              className="w-full border border-gray-300 rounded-lg p-2"
              value={styleName}
              onChange={handleStyleNameChange}
            />
          </div>
          {/* <div className="mb-6 relative" ref={dropdownRef}>
            <label htmlFor="category" className="block text-gray-700 mb-2">Choose a Category</label>
            <input
              type="text"
              id="category"
              placeholder="Ex. Modern Minimalist"
              className="w-full border border-gray-300 rounded-lg p-2"
              value={selectedCategory}
              onClick={() => setShowCategoryDropdown(true)}
              readOnly
            />
            {showCategoryDropdown && (
              <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
                <div className="p-2">
                  <input
                    type="text"
                    placeholder="Search for category"
                    className="w-full border border-gray-300 rounded-lg p-2 mb-2"
                  />
                </div>
                {Object.entries(categories).map(([mainCategory, subCategories]) => (
                  <div key={mainCategory} className="border-t border-gray-200">
                    <div className="p-3 font-medium flex items-center justify-between">
                      {mainCategory}
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    {Object.entries(subCategories).map(([subCategory, items]) => (
                      <div key={subCategory} className="pl-4">
                        <div className="p-2 font-medium text-gray-700">{subCategory}</div>
                        {items.map((item) => (
                          <div
                            key={item}
                            className="pl-4 p-2 hover:bg-gray-100 cursor-pointer flex items-center"
                            onClick={() => handleCategorySelect(item)}
                          >
                            <div className={`w-4 h-4 rounded-full border mr-2 flex items-center justify-center ${selectedCategory === item ? 'bg-purple-600 border-purple-600' : 'border-gray-300'}`}>
                              {selectedCategory === item && (
                                <div className="w-2 h-2 bg-white rounded-full" />
                              )}
                            </div>
                            {item}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div> */}

          <button
            className="bg-purple-600 text-white rounded-lg px-4 py-2 w-full hover:bg-purple-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed relative overflow-hidden"
            onClick={handleUpload}
            disabled={isUploading}
            aria-disabled={isUploading}
            style={{ pointerEvents: isUploading ? 'none' : 'auto' }}
          >
            {isUploading ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white mr-2"></div>
                <span>Creating...</span>
              </div>
            ) : 'Create'}
          </button>
        </div>

        <div>
          {uploadedImages.length === 0 ? (
            <div
              className="border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center p-8 bg-gray-50 file-upload h-full"
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
            >
              <i className="fas fa-cloud-upload-alt text-purple-600 text-4xl mb-4"></i>
              <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
              <p className="text-gray-400 mb-4">Max. File Size: 30MB</p>
              <button
                type="button"
                className="bg-purple-600 text-white rounded-lg px-4 py-2 hover:bg-purple-700 transition-colors duration-200"
                onClick={handleClick}
              >
                Browse File
              </button>
              <input
                type="file"
                ref={fileInputRef}
                multiple
                accept="image/*"
                className="hidden"
                onChange={handleChange}
              />
            </div>
          ) : (
            <div className="grid grid-cols-3 gap-4">
              <div
                className="h-24 border-2 border-dashed border-gray-300 rounded-lg flex flex-col items-center justify-center bg-gray-50 cursor-pointer hover:bg-gray-100 transition-all duration-200"
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                onClick={handleClick}
              >
                <i className="fas fa-cloud-upload-alt text-purple-600 text-xl"></i>
                <p className="text-gray-600 text-xs text-center mt-1">Upload Image</p>
                <input
                  type="file"
                  ref={fileInputRef}
                  multiple
                  accept="image/*"
                  className="hidden"
                  onChange={handleChange}
                />
              </div>
              {uploadedImages.map((image, index) => (
                <div key={index} className="relative group">
                  <img
                    src={image.preview}
                    alt={`Upload ${index + 1}`}
                    className="w-full h-24 object-cover rounded-lg"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-40 transition-all duration-300 rounded-lg flex items-center justify-center">
                    <button
                      onClick={() => removeImage(index)}
                      className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600"
                    >
                      <FaMinus size={12} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateNewStyle; 